import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Portal,
  Slide,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { PageHeader } from 'app/components/StyledComponents';
import {
  GuidanceTourStep,
  GuidanceTourStepCompletionMap,
  OnboardingChecklistItem,
  OnboardingStepAccessibilityMap,
  OnboardingStepValidationResult,
} from '../types';
import GuidanceTourChecklist from './GuidanceTourChecklist';
import LaunchReadinessIntro from './LaunchReadinessIntro';
import GuidanceTourSectionProgress, {
  GUIDANCE_TOUR_SECTION_PROGRESS_TOOLTIP_ATTR,
} from './GuidanceTourSectionProgress';
import {
  getOnboardingProgressColor,
  getOnboardingStepIconColor,
  getOnboardingStepTextColor,
} from '../utils/onboardingStepStatusColors';
import { getStatusChipColor } from 'app/utils/statusChipColors';
import { OnboardingStepResourceLinks } from './OnboardingStepResourceLinks';
import {
  getGuidanceTourLayoutInsets,
  GUIDANCE_TOUR_DRAWER_SLIDE_TIMEOUT_MS,
  GUIDANCE_TOUR_DRAWER_Z_INDEX,
} from '../utils/guidanceTourLayoutInsets';
import {
  CDP_SETTINGS_SETUP_STEP_ID,
  CRM_MAILGUN_ROUTES_CHECKLIST_ID,
  SANDBOX_READINESS_SETUP_STEP_ID,
  UNSOLD_TEST_LEAD_CHECKLIST_ID,
} from '../utils/onboardingStepContext';

interface GuidanceTourPanelProps {
  tourTitle: string;
  step: GuidanceTourStep;
  steps: GuidanceTourStep[];
  stepIndex: number;
  stepCompleted: GuidanceTourStepCompletionMap;
  stepAccessibility: OnboardingStepAccessibilityMap;
  isCurrentStepCompleted: boolean;
  canMarkCurrentStepComplete: boolean;
  priorIncompleteCount: number;
  canNavigateBack: boolean;
  canNavigateNext: boolean;
  progressPercent: number;
  showLaunchIntro: boolean;
  onDismissLaunchIntro: () => void;
  isPreparingStep: boolean;
  stepValidation: OnboardingStepValidationResult | null;
  isLoadingStepValidation: boolean;
  isMarkingComplete: boolean;
  markCompleteError: string | null;
  useApiValidation: boolean;
  onMarkComplete: () => void;
  onNext: () => void;
  onBack: () => void;
  onSaveAndExit: () => void;
  onRestart: () => void;
  onJumpToStep: (index: number) => void;
  onJumpToPrerequisiteStep: (stepId: string) => void;
  onRefreshValidation: () => void;
  crmSupportsEmailReports: boolean;
  crmSupportsEmailReportsSelection: boolean | null;
  sandboxReadinessRequired: boolean;
  sandboxReadinessRequiredSelection: boolean | null;
  isSavingStepContext: boolean;
  onCrmSupportsEmailReportsChange: (enabled: boolean) => void;
  onSandboxReadinessRequiredChange: (required: boolean) => void;
}

const STEPS_SECTION_HEIGHT_MS = 320;
const STEPS_SECTION_HEIGHT_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

const sectionScrollSx = {
  overflowY: 'auto' as const,
  boxSizing: 'border-box' as const,
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    bgcolor: 'action.disabled',
  },
};

const stepsSectionBaseSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  px: 2.75,
  pt: 0,
  pb: 0,
  boxSizing: 'border-box' as const,
};

const stepDetailSectionSx = {
  flexShrink: 0,
  borderTop: 1,
  borderColor: 'divider',
  px: 2.75,
  pt: 1,
  pb: 1.5,
  boxSizing: 'border-box' as const,
};

const GuidanceTourPanel: React.FC<GuidanceTourPanelProps> = ({
  tourTitle,
  step,
  steps,
  stepIndex,
  stepCompleted,
  stepAccessibility,
  isCurrentStepCompleted,
  canMarkCurrentStepComplete,
  priorIncompleteCount,
  canNavigateBack,
  canNavigateNext,
  progressPercent,
  showLaunchIntro,
  onDismissLaunchIntro,
  isPreparingStep,
  stepValidation,
  isLoadingStepValidation,
  isMarkingComplete,
  markCompleteError,
  useApiValidation,
  onMarkComplete,
  onNext,
  onBack,
  onSaveAndExit,
  onRestart,
  onJumpToStep,
  onJumpToPrerequisiteStep,
  onRefreshValidation,
  crmSupportsEmailReports,
  crmSupportsEmailReportsSelection,
  sandboxReadinessRequired,
  sandboxReadinessRequiredSelection,
  isSavingStepContext,
  onCrmSupportsEmailReportsChange,
  onSandboxReadinessRequiredChange,
}) => {
  const theme = useTheme();
  const panelRef = useRef<HTMLDivElement>(null);
  const isDrawerClosingRef = useRef(false);
  const layoutInsets = getGuidanceTourLayoutInsets();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [stepsSectionExpanded, setStepsSectionExpanded] = useState(false);
  const [stepsArrowExpanded, setStepsArrowExpanded] = useState(false);
  const [stepsNeedsScroll, setStepsNeedsScroll] = useState(false);
  const [isStepsHeightAnimating, setIsStepsHeightAnimating] = useState(false);
  const [stepsAnimatedHeight, setStepsAnimatedHeight] = useState<number | null>(null);
  const stepsExpandDirectionRef = useRef<'expand' | 'collapse'>('expand');
  const middleColumnRef = useRef<HTMLDivElement>(null);
  const stepsFlexAreaRef = useRef<HTMLDivElement>(null);
  const stepsContentRef = useRef<HTMLDivElement>(null);
  const stepsScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const stepsToggleRef = useRef<HTMLButtonElement>(null);
  const stepDetailRef = useRef<HTMLDivElement>(null);
  const isLastStep = stepIndex >= steps.length - 1;
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => setDrawerOpen(true));
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  const requestDrawerClose = useCallback(() => {
    if (isDrawerClosingRef.current) return;
    isDrawerClosingRef.current = true;
    if (drawerOpen) {
      setDrawerOpen(false);
      return;
    }
    isDrawerClosingRef.current = false;
    onSaveAndExit();
  }, [drawerOpen, onSaveAndExit]);

  const handleDrawerExited = useCallback(() => {
    if (!isDrawerClosingRef.current) return;
    isDrawerClosingRef.current = false;
    onSaveAndExit();
  }, [onSaveAndExit]);

  const stepsNeedsScrollRef = useRef(false);
  const stepsOverflowRafRef = useRef<number | null>(null);
  const isStepsHeightAnimatingRef = useRef(isStepsHeightAnimating);
  const stepsSectionExpandedRef = useRef(stepsSectionExpanded);

  isStepsHeightAnimatingRef.current = isStepsHeightAnimating;
  stepsSectionExpandedRef.current = stepsSectionExpanded;
  stepsNeedsScrollRef.current = stepsNeedsScroll;

  const getCollapsedStepsTargetHeight = () => {
    const middle = middleColumnRef.current;
    const toggle = stepsToggleRef.current;
    const detail = stepDetailRef.current;

    if (middle && detail) {
      const arrowHeight = stepsNeedsScroll ? (toggle?.offsetHeight ?? 0) : 0;
      return Math.max(
        80,
        middle.clientHeight - detail.offsetHeight - arrowHeight,
      );
    }

    return stepsFlexAreaRef.current?.clientHeight ?? 200;
  };

  const measureStepsNeedsScroll = useCallback(() => {
    const middle = middleColumnRef.current;
    const detail = stepDetailRef.current;
    const scrollEl = stepsScrollContainerRef.current;

    if (!middle || !detail) {
      return false;
    }

    const availableHeight = middle.clientHeight - detail.offsetHeight;
    const contentHeight =
      scrollEl?.scrollHeight ?? stepsContentRef.current?.scrollHeight ?? 0;

    if (contentHeight > availableHeight + 1) {
      return true;
    }

    if (scrollEl && scrollEl.clientHeight > 0) {
      return scrollEl.scrollHeight > scrollEl.clientHeight + 1;
    }

    return false;
  }, []);

  const scheduleStepsOverflowCheck = useCallback(() => {
    if (stepsOverflowRafRef.current !== null) {
      return;
    }

    stepsOverflowRafRef.current = window.requestAnimationFrame(() => {
      stepsOverflowRafRef.current = null;

      if (
        isStepsHeightAnimatingRef.current ||
        stepsSectionExpandedRef.current
      ) {
        return;
      }

      const nextNeedsScroll = measureStepsNeedsScroll();
      if (nextNeedsScroll === stepsNeedsScrollRef.current) {
        return;
      }

      stepsNeedsScrollRef.current = nextNeedsScroll;
      setStepsNeedsScroll(nextNeedsScroll);
    });
  }, [measureStepsNeedsScroll]);

  useLayoutEffect(() => {
    if (stepsSectionExpanded || isStepsHeightAnimating) {
      return undefined;
    }

    const nodes = [
      middleColumnRef.current,
      stepDetailRef.current,
      stepsScrollContainerRef.current,
      stepsContentRef.current,
    ].filter(Boolean) as Element[];

    if (nodes.length === 0) {
      return undefined;
    }

    scheduleStepsOverflowCheck();

    const resizeObserver = new ResizeObserver(() => {
      scheduleStepsOverflowCheck();
    });

    nodes.forEach(node => resizeObserver.observe(node));

    return () => {
      resizeObserver.disconnect();
      if (stepsOverflowRafRef.current !== null) {
        window.cancelAnimationFrame(stepsOverflowRafRef.current);
        stepsOverflowRafRef.current = null;
      }
    };
  }, [
    drawerOpen,
    isStepsHeightAnimating,
    scheduleStepsOverflowCheck,
    stepIndex,
    steps.length,
    stepsSectionExpanded,
  ]);

  useEffect(() => {
    if (!stepsNeedsScroll && !isStepsHeightAnimating && !stepsSectionExpanded) {
      setStepsArrowExpanded(false);
    }
  }, [stepsNeedsScroll, isStepsHeightAnimating, stepsSectionExpanded]);

  const handleStepsSectionToggle = () => {
    if (isStepsHeightAnimating || !stepsNeedsScroll) {
      return;
    }

    if (!stepsSectionExpanded) {
      const fromHeight =
        stepsFlexAreaRef.current?.clientHeight ?? getCollapsedStepsTargetHeight();
      stepsExpandDirectionRef.current = 'expand';
      setStepsArrowExpanded(true);
      middleColumnRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      stepsScrollContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      setIsStepsHeightAnimating(true);
      setStepsAnimatedHeight(fromHeight);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const toHeight = stepsContentRef.current?.scrollHeight ?? fromHeight;
          setStepsAnimatedHeight(toHeight);
        });
      });
      return;
    }

    const fromHeight =
      stepsFlexAreaRef.current?.offsetHeight ??
      stepsContentRef.current?.scrollHeight ??
      stepsFlexAreaRef.current?.clientHeight ??
      200;
    const toHeight = getCollapsedStepsTargetHeight();
    stepsExpandDirectionRef.current = 'collapse';
    setStepsArrowExpanded(false);
    setStepsSectionExpanded(false);
    middleColumnRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    stepsScrollContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    setIsStepsHeightAnimating(true);
    setStepsAnimatedHeight(fromHeight);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setStepsAnimatedHeight(getCollapsedStepsTargetHeight() || toHeight);
      });
    });
  };

  const handleStepsHeightTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    if (
      event.propertyName !== 'height' ||
      event.target !== event.currentTarget ||
      !isStepsHeightAnimating
    ) {
      return;
    }

    const willExpand = stepsExpandDirectionRef.current === 'expand';
    setIsStepsHeightAnimating(false);
    setStepsAnimatedHeight(null);
    setStepsSectionExpanded(willExpand);

    window.requestAnimationFrame(() => {
      middleColumnRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      stepsScrollContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });

      if (willExpand) {
        middleColumnRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const stepsUseContentHeight =
    !stepsNeedsScroll || stepsSectionExpanded || isStepsHeightAnimating;
  const stepsFillHeight =
    stepsNeedsScroll && !stepsSectionExpanded && !isStepsHeightAnimating;
  const showStepsExpandToggle =
    stepsNeedsScroll || stepsSectionExpanded || isStepsHeightAnimating;
  const isStepsExpandedLayout =
    stepsSectionExpanded && !isStepsHeightAnimating;

  const checklistItems: OnboardingChecklistItem[] =
    stepValidation?.checklist?.length
      ? stepValidation.checklist
      : (step.actionItems ?? []).map((label, index) => ({
          id: `${step.id}-fallback-${index}`,
          label,
          validated: false,
        }));
  const showCrmEmailReportsToggle = step.id === CDP_SETTINGS_SETUP_STEP_ID;
  const showSandboxReadinessToggle = step.id === SANDBOX_READINESS_SETUP_STEP_ID;
  const stepContextChoiceDisabled =
    isSavingStepContext || isLoadingStepValidation || isPreparingStep;
  const crmChoiceButtonSx = (selected: boolean) => ({
    minWidth: 44,
    height: 28,
    px: 1.25,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    textTransform: 'none' as const,
    boxShadow: 'none',
    ...(selected
      ? {}
      : {
          borderColor: theme.palette.divider,
          color: 'text.secondary',
        }),
  });
  const visibleChecklistItems = checklistItems.filter(item => {
    if (
      showCrmEmailReportsToggle &&
      item.id === CRM_MAILGUN_ROUTES_CHECKLIST_ID &&
      !crmSupportsEmailReports
    ) {
      return false;
    }
    if (
      showSandboxReadinessToggle &&
      item.id === UNSOLD_TEST_LEAD_CHECKLIST_ID &&
      !sandboxReadinessRequired
    ) {
      return false;
    }
    return true;
  });
  const showRequirementChips = Boolean(stepValidation?.checklist?.length);
  const resourceLinks = stepValidation?.resource_links ?? [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (restartConfirmOpen) return;

      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;

      const element = event.target as Element;
      if (element.closest?.('[data-guidance-tour-dependency-hint]')) {
        return;
      }
      if (element.closest?.(`[${GUIDANCE_TOUR_SECTION_PROGRESS_TOOLTIP_ATTR}]`)) {
        return;
      }
      if (
        element.closest?.(
          '.MuiDialog-root, .MuiModal-root, .MuiPopover-root, .MuiMenu-root',
        )
      ) {
        return;
      }

      requestDrawerClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [requestDrawerClose, restartConfirmOpen]);

  const navIconButtonSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    width: 36,
    height: 36,
    '&:hover': {
      bgcolor: 'action.hover',
    },
    '&.Mui-disabled': {
      borderColor: theme.palette.action.disabledBackground,
    },
  };

  const requirementChipSx = (required: boolean) => {
    const color = getStatusChipColor(theme, required ? 'FAILED' : 'WARNING');

    return {
      height: 16,
      fontSize: '0.5625rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
      flexShrink: 0,
      '& .MuiChip-label': {
        px: 0.5,
        py: 0,
      },
      bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.22 : 0.12),
      color,
    };
  };

  const checklistLabelSx = (validated: boolean) => ({
    flex: 1,
    minWidth: 0,
    fontSize: '0.75rem',
    lineHeight: 1.25,
    color: getOnboardingStepTextColor(theme, validated ? 'completed' : 'pending'),
    fontWeight: validated ? 500 : 400,
  });

  const completedStepCount = steps.filter(
    step => stepCompleted[step.id] === true,
  ).length;
  const headerProgressColor =
    progressPercent === 0
      ? getOnboardingStepTextColor(theme, 'pending')
      : getOnboardingProgressColor(theme, progressPercent) ??
        getOnboardingStepTextColor(theme, 'completed');

  return (
    <>
      <Portal>
        <Slide
          direction="left"
          in={drawerOpen}
          mountOnEnter
          unmountOnExit
          timeout={GUIDANCE_TOUR_DRAWER_SLIDE_TIMEOUT_MS}
          onExited={handleDrawerExited}
        >
          <Box
            ref={panelRef}
            sx={{
              position: 'fixed',
              top: layoutInsets.top,
              right: 0,
              width: layoutInsets.drawerWidth,
              maxWidth: `calc(100vw - ${layoutInsets.left}px)`,
              height: `calc(100vh - ${layoutInsets.top}px)`,
              zIndex: GUIDANCE_TOUR_DRAWER_Z_INDEX,
              bgcolor: 'background.paper',
              boxShadow: theme.shadows[16],
              display: 'flex',
              flexDirection: 'column',
            }}
          >
          {showLaunchIntro ? (
            <LaunchReadinessIntro
              tourTitle={tourTitle}
              stepCount={steps.length}
              onGettingStarted={onDismissLaunchIntro}
            />
          ) : (
            <>
          <Box
            sx={{
              flexShrink: 0,
              px: 2.5,
              pt: 2,
              pb: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.035),
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 1.25,
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  py: 0.25,
                }}
              >
                <PageHeader
                  component="h2"
                  sx={{
                    marginBottom: '0 !important',
                    height: 'auto',
                    minHeight: 32,
                    lineHeight: 1.25,
                  }}
                >
                  {tourTitle}
                </PageHeader>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mt: 0.375,
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                  }}
                >
                  {completedStepCount} of {steps.length} steps complete
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'stretch',
                  gap: 0.125,
                  px: 1.5,
                  minWidth: 58,
                  borderRadius: 2,
                  bgcolor: alpha(headerProgressColor, 0.12),
                  border: `1px solid ${alpha(headerProgressColor, 0.22)}`,
                  flexShrink: 0,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color: headerProgressColor,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {progressPercent}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    color: headerProgressColor,
                    opacity: 0.9,
                  }}
                >
                  %
                </Typography>
              </Box>
            </Box>

            <GuidanceTourSectionProgress
              steps={steps}
              stepCompleted={stepCompleted}
              stepAccessibility={stepAccessibility}
              stepIndex={stepIndex}
              onJumpToStep={onJumpToStep}
            />
          </Box>

          <Box
            ref={middleColumnRef}
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: isStepsExpandedLayout ? 'auto' : 'hidden',
              ...(isStepsExpandedLayout ? sectionScrollSx : {}),
            }}
          >
            <Box
              ref={stepsFlexAreaRef}
              onTransitionEnd={handleStepsHeightTransitionEnd}
              sx={{
                ...stepsSectionBaseSx,
                ...(isStepsHeightAnimating
                  ? {
                      flex: '0 0 auto',
                      height: stepsAnimatedHeight ?? 'auto',
                      overflow: 'hidden',
                      transition: `height ${STEPS_SECTION_HEIGHT_MS}ms ${STEPS_SECTION_HEIGHT_EASING}`,
                    }
                  : isStepsExpandedLayout
                    ? {
                        flex: '0 0 auto',
                        flexShrink: 0,
                        overflow: 'visible',
                      }
                    : showStepsExpandToggle
                      ? {
                          flex: '1 1 0',
                          minHeight: 0,
                          overflow: 'hidden',
                        }
                      : {
                          flex: '0 0 auto',
                          overflow: 'visible',
                        }),
              }}
            >
              <Box
                ref={stepsContentRef}
                sx={{
                  ...(stepsFillHeight
                    ? {
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }
                    : {}),
                }}
              >
                <GuidanceTourChecklist
                  steps={steps}
                  stepIndex={stepIndex}
                  stepCompleted={stepCompleted}
                  stepAccessibility={stepAccessibility}
                  open
                  fillHeight={stepsFillHeight}
                  useContentHeight={stepsUseContentHeight}
                  scrollContainerRef={stepsScrollContainerRef}
                  onJumpToStep={onJumpToStep}
                  onJumpToPrerequisiteStep={onJumpToPrerequisiteStep}
                />
              </Box>
            </Box>

            {showStepsExpandToggle && (
              <Tooltip
                title={
                  stepsArrowExpanded
                    ? 'Collapse steps list'
                    : 'Expand steps list to full height'
                }
              >
                <ButtonBase
                  ref={stepsToggleRef}
                  aria-label={
                    stepsArrowExpanded
                      ? 'Collapse steps list'
                      : 'Expand steps list'
                  }
                  aria-expanded={stepsArrowExpanded}
                  onClick={handleStepsSectionToggle}
                  sx={{
                    flexShrink: 0,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderTop: 1,
                    borderColor: 'divider',
                    py: 0.75,
                    cursor: 'pointer',
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.09),
                    },
                    '&:active': {
                      bgcolor: alpha(theme.palette.primary.main, 0.14),
                    },
                  }}
                >
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      color: 'text.secondary',
                      transform: stepsArrowExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: `transform ${STEPS_SECTION_HEIGHT_MS}ms ${STEPS_SECTION_HEIGHT_EASING}`,
                    }}
                  />
                </ButtonBase>
              </Tooltip>
            )}

            {!isStepsExpandedLayout &&
              !showStepsExpandToggle &&
              !isStepsHeightAnimating && (
                <Box sx={{ flex: '1 1 0', minHeight: 0 }} aria-hidden />
              )}

            <Box
              ref={stepDetailRef}
              sx={{
                ...stepDetailSectionSx,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: '1.0625rem', fontWeight: 600, minWidth: 0 }}
                >
                  <Box
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      mr: 0.75,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Step {stepIndex + 1}
                  </Box>
                  {step.title}
                </Typography>
                <OnboardingStepResourceLinks links={resourceLinks} />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.75, fontSize: '0.8125rem', lineHeight: 1.55 }}
              >
                {step.instruction}
              </Typography>

              {showCrmEmailReportsToggle && (
                <Box
                  sx={{
                    mb: 2,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.25,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.default',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.75,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Chip label="Required" size="small" sx={requirementChipSx(true)} />
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          lineHeight: 1.45,
                          color: 'text.primary',
                        }}
                      >
                        Does CRM support email reports?
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                      <Button
                        variant={crmSupportsEmailReportsSelection === true ? 'contained' : 'outlined'}
                        size="small"
                        color="primary"
                        disabled={stepContextChoiceDisabled}
                        onClick={() => onCrmSupportsEmailReportsChange(true)}
                        sx={crmChoiceButtonSx(crmSupportsEmailReportsSelection === true)}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={crmSupportsEmailReportsSelection === false ? 'contained' : 'outlined'}
                        size="small"
                        color="primary"
                        disabled={stepContextChoiceDisabled}
                        onClick={() => onCrmSupportsEmailReportsChange(false)}
                        sx={crmChoiceButtonSx(crmSupportsEmailReportsSelection === false)}
                      >
                        No
                      </Button>
                    </Box>
                  </Box>
                  {crmSupportsEmailReportsSelection === null ? (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ display: 'block', mt: 0.75, fontSize: '0.6875rem' }}
                    >
                      Select Yes or No to continue.
                    </Typography>
                  ) : null}
                </Box>
              )}

              {showSandboxReadinessToggle && (
                <Box
                  sx={{
                    mb: 2,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.25,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.default',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.75,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Chip label="Required" size="small" sx={requirementChipSx(true)} />
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          lineHeight: 1.45,
                          color: 'text.primary',
                        }}
                      >
                        Do you need sandbox readiness testing?
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                      <Button
                        variant={sandboxReadinessRequiredSelection === true ? 'contained' : 'outlined'}
                        size="small"
                        color="primary"
                        disabled={stepContextChoiceDisabled}
                        onClick={() => onSandboxReadinessRequiredChange(true)}
                        sx={crmChoiceButtonSx(sandboxReadinessRequiredSelection === true)}
                      >
                        Yes
                      </Button>
                      <Button
                        variant={sandboxReadinessRequiredSelection === false ? 'contained' : 'outlined'}
                        size="small"
                        color="primary"
                        disabled={stepContextChoiceDisabled}
                        onClick={() => onSandboxReadinessRequiredChange(false)}
                        sx={crmChoiceButtonSx(sandboxReadinessRequiredSelection === false)}
                      >
                        No
                      </Button>
                    </Box>
                  </Box>
                  {sandboxReadinessRequiredSelection === null ? (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ display: 'block', mt: 0.75, fontSize: '0.6875rem' }}
                    >
                      Select Yes or No to continue.
                    </Typography>
                  ) : null}
                </Box>
              )}

              {visibleChecklistItems.length > 0 && (
                <Box sx={{ mb: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase' }}
                    >
                      Checklist
                    </Typography>
                    {useApiValidation && (
                      <Tooltip title="Refresh checklist">
                        <span>
                          <IconButton
                            size="small"
                            onClick={onRefreshValidation}
                            disabled={isLoadingStepValidation || isPreparingStep}
                            aria-label="Refresh checklist"
                          >
                            <RefreshIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                  <Box component="ul" sx={{ m: 0, pl: 0, mb: 0, listStyle: 'none' }}>
                    {visibleChecklistItems.map(item => (
                      <Box
                        key={item.id}
                        component="li"
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          minHeight: 22,
                          py: 0.125,
                          mb: 0.125,
                          '&:last-child': { mb: 0 },
                        }}
                      >
                        {item.validated ? (
                          <CheckCircleIcon
                            sx={{
                              color: getOnboardingStepIconColor(theme, 'completed'),
                              mt: 0.0625,
                              flexShrink: 0,
                              fontSize: 14,
                            }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            sx={{
                              color: getOnboardingStepIconColor(theme, 'pending'),
                              mt: 0.0625,
                              flexShrink: 0,
                              fontSize: 14,
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.375,
                          }}
                        >
                          {showRequirementChips && (
                            <Chip
                              label={item.required === false ? 'Flag' : 'Required'}
                              size="small"
                              sx={requirementChipSx(item.required !== false)}
                            />
                          )}
                          <Typography component="span" sx={checklistLabelSx(item.validated)}>
                            {item.label}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {isLoadingStepValidation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Checking requirements...
                  </Typography>
                </Box>
              )}

              {isPreparingStep && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Loading step...
                  </Typography>
                </Box>
              )}

              {markCompleteError && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                  {markCompleteError}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.75,
                  mt: 2.25,
                  pb: 1,
                }}
              >
                {isCurrentStepCompleted ? (
                  <ButtonMAIQ
                    variant="outlined"
                    color="success"
                    size="small"
                    disabled
                    endIcon={<CheckCircleIcon fontSize="small" />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: theme.palette.success.main,
                      color: theme.palette.success.main,
                      '&.Mui-disabled': {
                        borderColor: theme.palette.success.light,
                        color: theme.palette.success.main,
                        opacity: 1,
                      },
                    }}
                  >
                    Marked complete
                  </ButtonMAIQ>
                ) : (
                  <Tooltip
                    title={
                      showCrmEmailReportsToggle && crmSupportsEmailReportsSelection === null
                        ? 'Select Yes or No for CRM email reports'
                        : showSandboxReadinessToggle && sandboxReadinessRequiredSelection === null
                          ? 'Select Yes or No for sandbox readiness'
                          : !canMarkCurrentStepComplete && isLastStep
                        ? `Complete all previous steps first (${priorIncompleteCount} remaining)`
                        : !canMarkCurrentStepComplete
                          ? 'Complete all required checklist items first'
                          : ''
                    }
                  >
                    <span>
                      <ButtonMAIQ
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={onMarkComplete}
                        disabled={
                          isPreparingStep ||
                          isLoadingStepValidation ||
                          isMarkingComplete ||
                          !canMarkCurrentStepComplete
                        }
                        startIcon={
                          isMarkingComplete ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : undefined
                        }
                      >
                        {isMarkingComplete
                          ? 'Saving...'
                          : isLastStep
                            ? 'Mark complete & finish'
                            : 'Mark complete'}
                      </ButtonMAIQ>
                    </span>
                  </Tooltip>
                )}
                {!isCurrentStepCompleted &&
                  isLastStep &&
                  priorIncompleteCount > 0 && (
                    <Typography variant="caption" color="warning.main">
                      {priorIncompleteCount} step
                      {priorIncompleteCount === 1 ? '' : 's'} remaining before you
                      can finish Launch Readiness
                    </Typography>
                  )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              px: 2.75,
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Tooltip title="Previous incomplete step">
                <span>
                  <IconButton
                    size="small"
                    onClick={onBack}
                    disabled={isPreparingStep || !canNavigateBack}
                    aria-label="Previous incomplete step"
                    sx={navIconButtonSx}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <ButtonMAIQ
                variant="text"
                color="inherit"
                size="small"
                onClick={() => setRestartConfirmOpen(true)}
                sx={{ minWidth: 0, px: 1 }}
              >
                Restart
              </ButtonMAIQ>

              <Tooltip title="Next incomplete step">
                <span>
                  <IconButton
                    size="small"
                    onClick={onNext}
                    disabled={isPreparingStep || !canNavigateNext}
                    aria-label="Next incomplete step"
                    sx={navIconButtonSx}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
            </>
          )}
          </Box>
        </Slide>
      </Portal>

      <ConfirmationDialogMAIQ
        open={restartConfirmOpen}
        onClose={() => setRestartConfirmOpen(false)}
        onConfirm={() => {
          setRestartConfirmOpen(false);
          onRestart();
        }}
        title="Restart Launch Readiness?"
        message="All saved step progress for this dealership will be reset."
        confirmLabel="Restart"
        loadingLabel="Restarting..."
        confirmColor="primary"
        cancelColor="primary"
      />
    </>
  );
};

export default GuidanceTourPanel;
